import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";
import PrivateRoute from "./privateRoutes";
import Logger from "../utils/logger";
import { Spin } from "antd";
import { useAuth } from "../store";
import AuthDebugger from "../utils/authDebugger";
import { Components } from "./routeConfig";
import { getInitialPath, getRoutesForUser } from "./routeUtils";
import RouteErrorBoundary from "../components/common/RouteErrorBoundary";
import { RoleTypes } from "../../types";
// Import LoginPage directly for faster initial load (not lazy loaded)
import LoginPage from "../pages/auth/Login";

// Lazy load MagicLinkVerify component
const MagicLinkVerifyPage = lazy(() => import("../pages/auth/MagicLinkVerify"));

function AppRoutes() {
  // Check authentication state properly
  const isAuthenticated = useAuth((state) => state.isAuthenticated);
  const isInitialized = useAuth((state) => state.isInitialized);
  const getSecureRole = useAuth((state) => state.getSecureRole);
  const getCapabilities = useAuth((state) => state.getCapabilities);
  const user = useAuth((state) => state.user);
  const fallbackRole = user.role;

  const role = (getSecureRole() || fallbackRole) as RoleTypes;
  const capabilities = getCapabilities();
  const advanceCapabilities = user.advanceCapabilities;

  // Debug role validation in development
  if (process.env.NODE_ENV === "development") {
    AuthDebugger.debugCurrentState();
    AuthDebugger.debugRouteAccess(window.location.pathname, role);
    Logger.dev("🔐 Authentication Debug", {
      isAuthenticated,
      isInitialized,
      role,
      secureRole: getSecureRole(),
      fallbackRole,
    });
  }

  // Show loading spinner until auth is initialized
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
        <span className="ml-2">Initializing...</span>
      </div>
    );
  }

  const router = createBrowserRouter(
    [
      {
        path: "/",
        element: (() => {
          if (!isAuthenticated) {
            return <Navigate to="/login" replace />;
          }

          const initialPath = getInitialPath(role);
          if (!initialPath) {
            // Role is invalid or Default_Role, redirect to login
            return <Navigate to="/login" replace />;
          }

          return <Navigate to={"/app" + initialPath} replace />;
        })(),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "app",
        element: <PrivateRoute />,
        children: getRoutesForUser(role, capabilities, advanceCapabilities),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "login",
        element: <LoginPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "magic-link",
        element: <MagicLinkVerifyPage />,
        errorElement: <RouteErrorBoundary />,
      },

      {
        path: "404",
        element: <Components.PageNotFound />,
      },
    ],
    {
      future: {
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: false,
      },
    },
  );

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <Spin />
        </div>
      }
    >
      <RouterProvider router={router} />
    </Suspense>
  );
}

export default AppRoutes;
